import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { getProgress } from "../lib/fashionProgress";
import { gaEvent } from "../lib/behavior";
import styles from "../../styles/FashionLibrary.module.css";

// Glamzy's spin wheel: a card that opens a wheel of every look. It lands on one (a look the visitor has not
// styled yet while there are any) and Play opens that look's game. The
// faces come from one picture and the list from one file (public/assets/glamzy-wheel.*, made by
// scripts/build-glam-library.js), loaded only when the wheel opens. The drawn disc turns as a whole
// (CSS transform); the rim, its bulbs, the pin and the star button stay still on top of it.
const WHEEL_DATA = "/assets/glamzy-wheel.json";
// the faces stand in three rings, so they can be much bigger than one slice is wide
const RINGS = [0.885, 0.745, 0.605];
const FACE = 0.082;
const BULBS = Array.from({ length: 16 }, (_, i) => i);
const SPIN_MS = 5200;
const TURNS = 6;
const MUTE_KEY = "glamzy.wheelMuted";
const TICK_GAP = 0.05; // seconds: the fast start of a spin ticks at this pace, not once per slice

// GA4 events: spin_wheel_open (the card), spin_wheel_spin, spin_wheel_result and spin_wheel_play, the last two with the look's slug

// The wheel's sounds are made here (Web Audio), so there are no sound files to load. The audio starts
// on the Spin tap, as browsers require.
function makeSounds() {
  const Ctx = typeof window !== "undefined" && (window.AudioContext || window.webkitAudioContext);
  if (!Ctx) return null;
  const ctx = new Ctx();
  const note = (freq, at, length, volume, type = "sine") => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(volume, at);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + length);
    osc.connect(gain).connect(ctx.destination);
    osc.start(at);
    osc.stop(at + length);
  };
  let lastTick = 0;
  return {
    wake: () => { if (ctx.state === "suspended") ctx.resume(); },
    close: () => { ctx.close(); },
    start: () => {
      const t = ctx.currentTime;
      note(330, t, 0.1, 0.14, "triangle");
      note(494, t + 0.07, 0.14, 0.14, "triangle");
    },
    tick: () => {
      const t = ctx.currentTime;
      if (t - lastTick < TICK_GAP) return;
      lastTick = t;
      note(1250, t, 0.035, 0.08, "square");
    },
    win: () => {
      const t = ctx.currentTime;
      [523, 659, 784, 1047].forEach((f, i) => note(f, t + i * 0.11, i === 3 ? 0.55 : 0.2, 0.16, "triangle"));
    },
  };
}

const readMuted = () => { try { return localStorage.getItem(MUTE_KEY) === "1"; } catch (e) { return false; } };
const saveMuted = (on) => { try { localStorage.setItem(MUTE_KEY, on ? "1" : "0"); } catch (e) { /* not kept */ } };

const shuffled = (list) => {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) {
    const k = Math.floor(Math.random() * (i + 1));
    [a[i], a[k]] = [a[k], a[i]];
  }
  return a;
};

// A look to land on, as a position on the wheel: not yet styled first, and not the one it just landed on
function choosePick(data, order, last) {
  const styled = getProgress().styled || {};
  const all = order.map((_, i) => i).filter((i) => data.looks[order[i]] !== last);
  const fresh = all.filter((i) => !styled[data.looks[order[i]].slug]);
  const pool = fresh.length ? fresh : all;
  return pool[Math.floor(Math.random() * pool.length)];
}

function drawWheel(canvas, sheet, data, order) {
  const size = canvas.width;
  const c = size / 2;
  const R = c;
  const n = order.length;
  const step = (Math.PI * 2) / n;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, size, size);
  order.forEach((_, i) => {
    ctx.save();
    ctx.translate(c, c);
    ctx.rotate(i * step);
    // its slice, with the first one straight up; the colours run round the wheel as a rainbow
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.arc(0, 0, R, -Math.PI / 2 - step / 2 - 0.004, -Math.PI / 2 + step / 2 + 0.004);
    ctx.closePath();
    const hue = (i * 360) / n;
    const fill = ctx.createRadialGradient(0, 0, R * 0.2, 0, 0, R);
    fill.addColorStop(0, `hsl(${hue}, 95%, 76%)`);
    fill.addColorStop(1, `hsl(${hue}, 90%, ${i % 2 ? 54 : 60}%)`);
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.restore();
  });
  // the soft pale circle round the star button
  ctx.beginPath();
  ctx.arc(c, c, R * 0.33, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(255, 255, 255, 0.32)";
  ctx.fill();
  order.forEach((look, i) => {
    const y = -R * RINGS[i % RINGS.length];
    const r = R * FACE;
    ctx.save();
    ctx.translate(c, c);
    ctx.rotate(i * step);
    ctx.beginPath();
    ctx.arc(0, y, r, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255, 255, 255, 0.95)";
    ctx.shadowColor = "rgba(70, 20, 60, 0.4)";
    ctx.shadowBlur = size * 0.008;
    ctx.shadowOffsetY = size * 0.003;
    ctx.fill();
    ctx.shadowColor = "transparent";
    const inner = r * 0.87;
    ctx.beginPath();
    ctx.arc(0, y, inner, 0, Math.PI * 2);
    ctx.clip();
    const sx = (look % data.cols) * data.cell;
    const sy = Math.floor(look / data.cols) * data.cell;
    ctx.drawImage(sheet, sx, sy, data.cell, data.cell, -inner, y - inner, inner * 2, inner * 2);
    ctx.restore();
  });
}

function Pin({ className }) {
  return (
    <svg className={className} viewBox="0 0 60 84" aria-hidden="true">
      <defs>
        <linearGradient id="wheel-pin" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FFE873" />
          <stop offset="1" stopColor="#F59A0C" />
        </linearGradient>
      </defs>
      <path d="M30 82 C24 66 4 50 4 29 A26 26 0 1 1 56 29 C56 50 36 66 30 82 Z" fill="url(#wheel-pin)" stroke="#B8650A" strokeWidth="3" strokeLinejoin="round" />
      <circle cx="30" cy="29" r="13" fill="#FF4D3D" stroke="#FFF3C4" strokeWidth="4" />
      <circle cx="25" cy="24" r="3.4" fill="#fff" opacity="0.85" />
    </svg>
  );
}

function Star({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
      <path d="M24 5 L29.6 17.2 L43 18.8 L33 27.9 L35.8 41 L24 34.4 L12.2 41 L15 27.9 L5 18.8 L18.4 17.2 Z"
        fill="#FF6A2B" stroke="#C93C0B" strokeWidth="3" strokeLinejoin="round" />
    </svg>
  );
}

function WheelPopup({ onClose }) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [phase, setPhase] = useState("ready"); // ready | spinning | landed
  const [winner, setWinner] = useState(null);
  const canvas = useRef(null);
  const order = useRef([]);
  const name = useRef(null);
  const angle = useRef(0);
  const frame = useRef(0);
  const timer = useRef(0);
  const closeBtn = useRef(null);
  const sounds = useRef(null);
  const [muted, setMuted] = useState(readMuted);
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const sound = (kind) => { if (sounds.current && !mutedRef.current) sounds.current[kind](); };

  const toggleMute = () => {
    saveMuted(!muted);
    setMuted(!muted);
  };

  useEffect(() => {
    let alive = true;
    const img = new Image();
    fetch(WHEEL_DATA).then((r) => r.json()).then((d) => {
      img.onload = () => {
        if (!alive) return;
        order.current = shuffled(d.looks.map((_, i) => i));
        setData(d);
        if (canvas.current) drawWheel(canvas.current, img, d, order.current);
      };
      img.onerror = () => { if (alive) setFailed(true); };
      img.src = d.sheet;
    }).catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (closeBtn.current) closeBtn.current.focus();
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      cancelAnimationFrame(frame.current);
      clearTimeout(timer.current);
      if (sounds.current) sounds.current.close();
    };
  }, [onClose]);

  const spin = () => {
    if (!data || phase === "spinning") return;
    const n = order.current.length;
    const step = 360 / n;
    const pick = choosePick(data, order.current, winner);
    const look = data.looks[order.current[pick]];
    gaEvent("spin_wheel_spin", { again: phase === "landed" ? 1 : 0 });
    new Image().src = look.cover; // ready for the moment it lands
    if (!sounds.current) sounds.current = makeSounds();
    if (sounds.current) sounds.current.wake();
    sound("start");

    const nudge = (Math.random() - 0.5) * step * 0.7;
    const now = ((angle.current % 360) + 360) % 360;
    const target = (((360 - pick * step + nudge) % 360) + 360) % 360;
    const to = angle.current + TURNS * 360 + ((target - now + 360) % 360);
    angle.current = to;
    setWinner(null);
    setPhase("spinning");

    const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const el = canvas.current;
    el.style.transition = reduced ? "none" : `transform ${SPIN_MS}ms cubic-bezier(0.12, 0.65, 0.08, 1)`;
    el.style.transform = `rotate(${to}deg)`;

    // the name under the wheel follows the look at the pin, with a tick as each slice passes it
    let under = -1;
    const track = () => {
      const m = new DOMMatrix(getComputedStyle(el).transform);
      const deg = (Math.atan2(m.b, m.a) * 180) / Math.PI;
      const i = ((Math.round(-deg / step) % n) + n) % n;
      if (i !== under) {
        if (under !== -1) sound("tick");
        under = i;
        if (name.current) name.current.textContent = data.looks[order.current[i]].title;
      }
      frame.current = requestAnimationFrame(track);
    };
    if (!reduced) frame.current = requestAnimationFrame(track);
    timer.current = setTimeout(() => {
      cancelAnimationFrame(frame.current);
      sound("win");
      gaEvent("spin_wheel_result", { look: look.slug });
      setWinner(look);
      setPhase("landed");
    }, reduced ? 50 : SPIN_MS + 60);
  };

  return (
    <div className={styles.wheelOverlay} onClick={onClose}>
      <div className={styles.wheelPanel} role="dialog" aria-modal="true" aria-label="Spin the wheel" onClick={(e) => e.stopPropagation()}>
        <button type="button" ref={closeBtn} className={styles.wheelClose} onClick={onClose} aria-label="Close">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true"><path d="M6 6 L18 18 M18 6 L6 18" /></svg>
        </button>
        <button type="button" className={`${styles.wheelClose} ${styles.wheelSound}`} onClick={toggleMute}
          aria-label={muted ? "Turn sound on" : "Turn sound off"} aria-pressed={muted}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 9.5 V14.5 H8 L13 19 V5 L8 9.5 Z" />
            {muted
              ? <path d="M17 9.5 L22 14.5 M22 9.5 L17 14.5" fill="none" />
              : <path d="M16.5 9 a4.2 4.2 0 0 1 0 6 M19 6.5 a7.8 7.8 0 0 1 0 11" fill="none" />}
          </svg>
        </button>
        <h2 className={styles.wheelTitle}>Spin the Wheel</h2>
        <p className={styles.wheelNote}>{data ? `All ${data.looks.length} themes are on the wheel. Spin, and play the one it picks!` : " "}</p>

        <div className={`${styles.wheelBox} ${phase === "spinning" ? styles.wheelSpinning : ""}`}>
          <span className={styles.wheelRim} aria-hidden="true" />
          <canvas ref={canvas} className={styles.wheelDisc} width="840" height="840" aria-hidden="true" />
          {BULBS.map((i) => (
            <span key={i} className={`${styles.wheelBulb} ${i % 2 ? styles.wheelBulbAlt : ""}`} style={{ "--a": `${i * 22.5}deg` }} aria-hidden="true" />
          ))}
          <Pin className={styles.wheelPin} />
          <button type="button" className={styles.wheelHub} onClick={spin} disabled={!data || phase === "spinning"}
            aria-label={phase === "landed" ? "Spin again" : "Spin"}>
            <Star className={styles.wheelStar} />
          </button>
          {/* the winner fills the whole wheel, inside its rim */}
          {phase === "landed" && winner && <img className={styles.wheelReveal} src={winner.cover} alt={winner.title} />}
        </div>

        <div className={styles.wheelResult} aria-live="polite">
          {failed && <span className={styles.wheelName}>The wheel could not load. Please try again.</span>}
          {!failed && phase === "ready" && (
            <button type="button" className={styles.wheelPlay} onClick={spin} disabled={!data}>{data ? "Spin" : "Loading..."}</button>
          )}
          {phase === "spinning" && <span ref={name} className={styles.wheelName}>&nbsp;</span>}
          {phase === "landed" && winner && (
            <>
              <span className={styles.wheelWinText}>
                <span className={styles.wheelWinName}>{winner.title}</span>
                <span className={styles.wheelWorld}>{winner.world}</span>
              </span>
              <span className={styles.wheelBtns}>
                <Link href={`/glamzy/${winner.slug}?play`} prefetch={false} className={styles.wheelPlay}
                  onClick={() => gaEvent("spin_wheel_play", { look: winner.slug })}>Play</Link>
                <button type="button" className={styles.wheelAgain} onClick={spin}>Spin again</button>
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// The first card of the home page's Glamzy row.
export default function SpinWheelCard() {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  return (
    <>
      <button type="button" className={`${styles.card} ${styles.cardBig} ${styles.cardBtn} ${styles.spinCard}`} onClick={() => { gaEvent("spin_wheel_open"); setOpen(true); }}>
        <span className={`${styles.cover} ${styles.spinCover}`}>
          <span className={styles.spinFrame} aria-hidden="true">
            <span className={styles.spinArt} />
            <span className={styles.spinHub}><Star className={styles.wheelStar} /></span>
          </span>
          <Pin className={styles.spinPin} />
        </span>
        <span className={styles.spinStrip} aria-hidden="true" />
        <span className={styles.cardText}>
          <span className={styles.cardTitle}>Spin the Wheel</span>
          <span className={styles.cardMeta}>Let it pick your theme</span>
        </span>
      </button>
      {open && createPortal(<WheelPopup onClose={close} />, document.body)}
    </>
  );
}
